import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  DetailedHTMLProps,
  FC,
  ReactNode,
} from 'react';
import React, { isValidElement } from 'react';
import type { ButtonProps as AriaButtonProps } from 'react-aria-components';
import { Button as AriaButton, Link as AriaLink } from 'react-aria-components';

import { Tooltip, TooltipTrigger } from '@/lib/base/tooltip/tooltip';
import { cx, sortCx } from '@/utils/cx';
import { isReactComponent } from '@/utils/is-react-component';
import { isExternalHref } from '@/utils/isExternalHref';

// eslint-disable-next-line react-refresh/only-export-components -- styles constant co-located with Button component
export const styles = sortCx({
  common: {
    root: [
      'group relative inline-flex h-max cursor-pointer items-center justify-center whitespace-nowrap outline-brand transition duration-100 ease-linear before:absolute focus-visible:outline-2 focus-visible:outline-offset-2',
      // When button is used within `InputGroup`
      'in-data-input-wrapper:shadow-xs in-data-input-wrapper:focus:!z-50 in-data-input-wrapper:in-data-leading:-mr-px in-data-input-wrapper:in-data-leading:rounded-r-none in-data-input-wrapper:in-data-leading:before:rounded-r-none in-data-input-wrapper:in-data-trailing:-ml-px in-data-input-wrapper:in-data-trailing:rounded-l-none in-data-input-wrapper:in-data-trailing:before:rounded-l-none',
      // Disabled styles
      'disabled:cursor-not-allowed disabled:text-fg-disabled data-disabled:cursor-not-allowed data-disabled:text-fg-disabled',
      // Icon styles
      'disabled:*:data-icon:text-fg-disabled_subtle',
      // Same as `icon` but for SSR icons that cannot be passed to the client as functions.
      // Icon SIZE lives on each `sizes` entry (size-4 at `xs`, size-5 from `sm`
      // up) per VISUAL_STYLE_GUIDE.md § size ladder — never re-hardcode it here.
      '*:data-icon:pointer-events-none *:data-icon:shrink-0 *:data-icon:transition-inherit-all',
    ].join(' '),
    icon: 'pointer-events-none shrink-0 transition-inherit-all',
  },
  paddingX: {
    xs: '!px-2',
    sm: '!px-3',
    md: '!px-3.5',
    lg: '!px-4',
    xl: '!px-4.5',
  },
  paddingY: {
    xs: '!py-1.5',
    sm: '!py-2',
    md: '!py-2.5',
    lg: '!py-2.5',
    xl: '!py-3',
  },
  // Heights are aligned to the shared field-control ladder in
  // forms/select/select-shared.ts, so a Button and an Input at the same `size`
  // are the same height:
  //
  //   xs 30/32   sm 36/40   md 40/44   lg 44/48   xl 48/56   (mobile/desktop)
  //
  // Buttons already matched that ladder's MOBILE column; what they lacked was
  // its desktop step, which is why callers hand-padded them to line up with
  // fields (see the `py='lg'` workaround in TeamManagementTab). Height is
  // `2 x py + line-height`, so the responsive step is carried on `py` and the
  // existing type scale is untouched — except `xs`, where 18px `text-xs`
  // cannot reach 32px at any clean padding step and so takes `sm:text-sm`,
  // exactly as the field scale's `xs` does.
  //
  // `data-icon-only:` padding is deliberately NOT responsive: those buttons
  // are square around a fixed 20px glyph and cannot hit the mobile column at a
  // Tailwind step. They sit at the desktop height at all widths.
  sizes: {
    xs: {
      root: [
        'gap-1 rounded-lg px-2.5 py-1.5 text-xs sm:text-sm font-semibold before:rounded-[7px] data-icon-only:p-1.5',
        '*:data-icon:size-4',
        'in-data-input-wrapper:px-3 in-data-input-wrapper:py-2 in-data-input-wrapper:data-icon-only:p-2',
      ].join(' '),
      linkRoot: 'gap-1',
    },
    sm: {
      root: [
        'gap-1 rounded-lg px-3 py-2 sm:py-2.5 text-sm font-semibold before:rounded-[7px] data-icon-only:p-2',
        '*:data-icon:size-5',
        'in-data-input-wrapper:px-3.5 in-data-input-wrapper:py-2.5 in-data-input-wrapper:data-icon-only:p-2.5',
      ].join(' '),
      linkRoot: 'gap-1',
    },
    md: {
      root: [
        'gap-1 rounded-lg px-3.5 py-2.5 sm:py-3 text-sm font-semibold before:rounded-[7px] data-icon-only:p-2.5',
        '*:data-icon:size-5',
        'in-data-input-wrapper:gap-1.5 in-data-input-wrapper:px-4 in-data-input-wrapper:text-md in-data-input-wrapper:data-icon-only:p-3',
      ].join(' '),
      linkRoot: 'gap-1',
    },
    lg: {
      root: 'gap-1.5 rounded-lg px-4 py-2.5 sm:py-3 text-md font-semibold before:rounded-[7px] data-icon-only:p-3 *:data-icon:size-5',
      linkRoot: 'gap-1.5',
    },
    xl: {
      root: 'gap-1.5 rounded-lg px-4.5 py-3 sm:py-4 text-md font-semibold before:rounded-[7px] data-icon-only:p-3.5 *:data-icon:size-5',
      linkRoot: 'gap-1.5',
    },
  },

  colors: {
    primary: {
      root: [
        'bg-brand-solid text-white shadow-xs-skeumorphic ring-1 ring-transparent ring-inset hover:bg-brand-solid_hover data-loading:bg-brand-solid_hover',
        // Inner border gradient
        'before:absolute before:inset-px before:border before:border-white/12 before:mask-b-from-0%',
        // Disabled styles
        'disabled:bg-disabled disabled:shadow-xs disabled:ring-disabled_subtle',
        // Icon styles
        '*:data-icon:text-button-primary-icon hover:*:data-icon:text-button-primary-icon_hover',
      ].join(' '),
    },
    secondary: {
      root: [
        'bg-primary text-secondary shadow-xs-skeumorphic ring-1 ring-primary ring-inset hover:bg-primary_hover hover:text-secondary_hover data-loading:bg-primary_hover',
        // Disabled styles
        'disabled:shadow-xs disabled:ring-disabled_subtle',
        // Icon styles
        '*:data-icon:text-fg-quaternary hover:*:data-icon:text-fg-quaternary_hover',
      ].join(' '),
    },
    tertiary: {
      root: [
        'text-tertiary hover:bg-primary_hover hover:text-tertiary_hover data-loading:bg-primary_hover',
        // Icon styles
        '*:data-icon:text-fg-quaternary hover:*:data-icon:text-fg-quaternary_hover',
      ].join(' '),
    },
    'link-gray': {
      root: [
        'justify-normal rounded p-0! text-tertiary hover:text-tertiary_hover',
        // Inner text underline
        '*:data-text:underline *:data-text:decoration-transparent *:data-text:underline-offset-2 hover:*:data-text:decoration-current',
        // Icon styles
        '*:data-icon:text-fg-quaternary hover:*:data-icon:text-fg-quaternary_hover',
      ].join(' '),
    },
    'link-color': {
      root: [
        'justify-normal rounded p-0! text-brand-secondary hover:text-brand-secondary_hover',
        // Inner text underline
        '*:data-text:underline *:data-text:decoration-transparent *:data-text:underline-offset-2 hover:*:data-text:decoration-current',
        // Icon styles
        '*:data-icon:text-fg-brand-secondary_alt hover:*:data-icon:text-fg-brand-secondary_hover',
      ].join(' '),
    },
    'brand-soft': {
      root: [
        'bg-utility-brand-50 text-utility-brand-600 shadow-xs ring-1 ring-utility-brand-200 ring-inset hover:bg-utility-brand-100 hover:ring-utility-brand-600 data-loading:bg-utility-brand-100',
        // Disabled styles
        'disabled:bg-disabled disabled:shadow-xs disabled:ring-disabled_subtle',
        // Icon styles
        '*:data-icon:text-utility-brand-600 hover:*:data-icon:text-utility-brand-700',
      ].join(' '),
    },
    'gray-soft': {
      root: [
        'bg-bg-secondary text-fg-secondary shadow-xs ring-1 ring-border-secondary ring-inset hover:bg-bg-secondary_hover hover:ring-border-primary data-loading:bg-bg-secondary_hover',
        // Disabled styles
        'disabled:bg-disabled disabled:shadow-xs disabled:ring-disabled_subtle',
        // Icon styles
        '*:data-icon:text-fg-quaternary hover:*:data-icon:text-fg-tertiary',
      ].join(' '),
    },
    'primary-destructive': {
      root: [
        'bg-error-solid text-white shadow-xs-skeumorphic ring-1 ring-transparent outline-error ring-inset',
        // Inner border gradient
        'before:absolute before:inset-px before:border before:border-white/12 before:mask-b-from-0%',
        // Disabled styles
        'disabled:bg-disabled disabled:shadow-xs disabled:ring-disabled_subtle',
        // Icon styles
        '*:data-icon:text-button-destructive-primary-icon hover:*:data-icon:text-button-destructive-primary-icon_hover',
      ].join(' '),
    },
    'secondary-destructive': {
      root: [
        'bg-primary text-error-primary shadow-xs-skeumorphic ring-1 ring-error_subtle outline-error ring-inset hover:bg-error-primary hover:text-error-primary_hover data-loading:bg-error-primary',
        // Disabled styles
        'disabled:bg-primary disabled:shadow-xs disabled:ring-disabled_subtle',
        // Icon styles
        '*:data-icon:text-fg-error-secondary hover:*:data-icon:text-fg-error-primary',
      ].join(' '),
    },
    'tertiary-destructive': {
      root: [
        'text-error-primary outline-error hover:bg-error-primary hover:text-error-primary_hover data-loading:bg-error-primary',
        // Icon styles
        '*:data-icon:text-fg-error-secondary hover:*:data-icon:text-fg-error-primary',
      ].join(' '),
    },
    'link-destructive': {
      root: [
        'justify-normal rounded p-0! text-error-primary outline-error hover:text-error-primary_hover',
        // Inner text underline
        '*:data-text:underline *:data-text:decoration-transparent *:data-text:underline-offset-2 hover:*:data-text:decoration-current',
        // Icon styles
        '*:data-icon:text-fg-error-secondary hover:*:data-icon:text-fg-error-primary',
      ].join(' '),
    },
  },
});

/**
 * Common props shared between button and anchor variants
 */
export interface CommonProps {
  /** Disables the button and shows a disabled state */
  isDisabled?: boolean;
  /** Shows a loading spinner and disables the button */
  isLoading?: boolean;
  /** The size variant of the button */
  size?: keyof typeof styles.sizes;
  /** The color variant of the button */
  color?: keyof typeof styles.colors;
  /** Icon component or element to show before the text */
  iconLeading?: FC<{ className?: string }> | ReactNode;
  /** Additional className merged with the default icon class for the leading icon */
  iconLeadingClassName?: string;
  /** Icon component or element to show after the text */
  iconTrailing?: FC<{ className?: string }> | ReactNode;
  /** Additional className merged with the default icon class for the trailing icon */
  iconTrailingClassName?: string;
  /** Removes horizontal padding from the text content */
  noTextPadding?: boolean;
  /** When true, keeps the text visible during loading state */
  showTextWhileLoading?: boolean;
  /** Override horizontal padding */
  px?: keyof typeof styles.paddingX;
  /** Override vertical padding */
  py?: keyof typeof styles.paddingY;
  /** Tooltip content shown when the button is disabled */
  disabledTooltip?: ReactNode;
}

/**
 * Props for the button variant (non-link)
 */
export interface ButtonProps
  extends
    CommonProps,
    DetailedHTMLProps<
      Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color' | 'slot'>,
      HTMLButtonElement
    > {
  /** Slot name for react-aria component */
  slot?: AriaButtonProps['slot'];
}

/**
 * Props for the link variant (anchor tag)
 */
interface LinkProps
  extends
    CommonProps,
    DetailedHTMLProps<
      Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'color'>,
      HTMLAnchorElement
    > {}

/** Union type of button and link props */
export type Props = ButtonProps | LinkProps;

export const Button = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  Props
>(function Button(rawProps, forwardedRef) {
  const {
    size = 'sm',
    color = 'primary',
    children,
    className,
    noTextPadding: noTextPaddingProp,
    px,
    py,
    iconLeading: IconLeading,
    iconLeadingClassName,
    iconTrailing: IconTrailing,
    iconTrailingClassName,
    isDisabled: disabled,
    isLoading: loading,
    showTextWhileLoading,
    disabledTooltip,
    title,
    ...otherProps
  } = rawProps;
  let noTextPadding = noTextPaddingProp;

  const hasHref = 'href' in otherProps;
  const href = hasHref ? otherProps.href : undefined;
  // External links (absolute URLs, mailto:, tel:) must render as a native
  // anchor — see isExternalHref() for why. Internal hrefs keep using
  // `AriaLink` so client-side navigation is preserved.
  let Component: React.ElementType = AriaButton;
  if (isExternalHref(href)) {
    Component = 'a';
  } else if (hasHref) {
    Component = AriaLink;
  }

  const isIcon =
    (IconLeading || IconTrailing) &&
    (children === null || children === undefined);
  const isLinkType = ['link-gray', 'link-color', 'link-destructive'].includes(
    color
  );

  noTextPadding = isLinkType || noTextPadding;

  // isGated: unified stable-element / onClick-gate path for non-href buttons
  // that carry a disabledTooltip. Links (hasHref) keep the existing branch.
  // Value-based (recomputed every render) — decides which PROPS the single
  // AriaButton gets (native `isDisabled` vs. `aria-disabled` + click gate),
  // which is safe to change on every render since it never changes the
  // *shape* of what Button returns.
  const isGated = disabledTooltip !== undefined && !hasHref;

  // wrapInTooltip: decides the *shape* of what Button returns (bare
  // AriaButton vs. Tooltip-wrapped AriaButton) — this must NOT be re-derived
  // from `disabledTooltip`'s value like `isGated` above. Some consumers (e.g.
  // SubmitButton) always pass the `disabledTooltip` prop but compute its
  // *value* from transient state (a submit error) that can toggle between
  // `undefined` and a ReactNode independently of `isDisabled` — outside of
  // any press. Re-deriving the wrapping from that value would flip the
  // returned tree shape on that toggle, remounting the underlying element
  // (losing focus, dropping in-flight `usePress` state, and detaching any
  // outstanding DOM reference to it) exactly like the disabled/enabled
  // instability this path exists to prevent. Basing it instead on whether the
  // caller wired the `disabledTooltip` prop AT ALL — via `in`, which sees the
  // key even when its value is `undefined` — is stable for that consumer's
  // entire lifetime, so the tree shape never changes after mount.
  const wrapInTooltip = 'disabledTooltip' in rawProps && !hasHref;

  // --- Hooks — ALL before any early return (Rules of Hooks) ---

  // Mirror of `disabled` so handleClick's gate always reads the latest value
  // without capturing a stale closure. Written directly in render body; safe
  // for refs (ref.current write has no observable render side-effect).
  const latestDisabledRef = React.useRef(disabled);
  latestDisabledRef.current = disabled;

  // Whether the most recent press-start occurred while the button was enabled.
  // Starts false so a bare click with no preceding press-start is judged only
  // by "enabled now" — a steadily-disabled button never fires.
  const pressStartedEnabledRef = React.useRef(false);

  // Capture press-start via react-aria's first-class onPressStart prop.
  // Fires for both pointer and keyboard; de-duped by usePress (held-Space
  // auto-repeat is collapsed). Works while aria-disabled because we omit
  // react-aria isDisabled in the gated path, keeping usePress fully active.
  const capturePressStart = React.useCallback(() => {
    pressStartedEnabledRef.current = !latestDisabledRef.current;
  }, []);

  // Pull onClick out of otherProps so we can wrap it in the gate below.
  // In non-gated paths we spread the original otherProps (which still
  // includes onClick), so normal buttons continue to fire unchanged.
  const { onClick: userOnClick, ...restProps } = otherProps;

  // Gated click: fire iff (enabled now) OR (press started while enabled).
  // Resetting pressStartedEnabledRef on every call ensures a later
  // synthetic/programmatic click without a fresh press-start is judged
  // only by "enabled now". aria-disabled does NOT block DOM clicks — this
  // gate is the sole suppression mechanism for the steadily-disabled case.
  const handleClick = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const allowed =
        !latestDisabledRef.current || pressStartedEnabledRef.current;
      pressStartedEnabledRef.current = false;
      if (!allowed) {
        e.preventDefault();
        return;
      }
      // Cast is safe: isGated implies !hasHref (button element, not anchor).
      (userOnClick as React.MouseEventHandler<HTMLButtonElement> | undefined)?.(
        e
      );
    },
    [userOnClick]
  );

  // --- End hooks ---

  let props = {};

  if (href) {
    props = {
      ...otherProps,

      href: disabled ? undefined : href,

      // Since anchor elements do not support the `disabled` attribute and state,
      // we need to specify `data-rac` and `data-disabled` in order to be able
      // to use the `disabled:` selector in classes.
      ...(disabled ? { 'data-rac': true, 'data-disabled': true } : {}),
    };
  } else if (isGated) {
    // Gated path: keep a single AriaButton at a stable tree position so an
    // in-flight press (started while enabled) survives a mid-press disable flip.
    //
    // Key invariants:
    //  • No react-aria `isDisabled` — that stamps the native `disabled` attr,
    //    removes focusability, blurs the element, and cancels an in-flight press.
    //  • `aria-disabled='true'` instead — preserves focusability and tooltip
    //    trigger, while communicating disabled state to assistive technology.
    //  • onClick → handleClick (gates the user callback via pressStartedEnabledRef).
    //  • onPressStart → capturePressStart (records whether the press began while
    //    enabled, so handleClick can allow a mid-press flip to complete).
    //
    // Compute aria-label fallback for icon-only buttons in the disabled state
    // (mirrors the old TooltipTrigger branch: aria-label → string children →
    // string disabledTooltip).
    const gatedIconAriaLabel =
      isIcon && disabled
        ? ((restProps as { 'aria-label'?: string })['aria-label'] ??
          (typeof children === 'string' ? children : undefined) ??
          (typeof disabledTooltip === 'string' ? disabledTooltip : undefined))
        : undefined;

    props = {
      ...restProps,

      type: (restProps as { type?: string }).type || 'button',
      isPending: loading,
      // aria-disabled instead of isDisabled (see comment above)
      'aria-disabled': disabled ? ('true' as const) : undefined,
      onClick: handleClick,
      onPressStart: capturePressStart,
      ...(gatedIconAriaLabel !== undefined
        ? { 'aria-label': gatedIconAriaLabel }
        : {}),
    };
  } else {
    // Non-gated path: original behavior, unchanged.
    props = {
      ...otherProps,

      type: otherProps.type || 'button',
      isPending: loading,
      isDisabled: disabled,
    };
  }

  const buttonElement = (
    <Component
      data-loading={loading ? true : undefined}
      data-icon-only={isIcon ? true : undefined}
      {...props}
      className={cx(
        styles.common.root,
        styles.sizes[size].root,
        styles.colors[color].root,
        isLinkType && styles.sizes[size].linkRoot,
        px && styles.paddingX[px],
        py && styles.paddingY[py],
        (loading || (href && (disabled || loading))) && 'pointer-events-none',
        // If in `loading` state, hide everything except the loading icon (and text if `showTextWhileLoading` is true).
        loading &&
          (showTextWhileLoading
            ? '[&>*:not([data-icon=loading]):not([data-text])]:hidden'
            : '[&>*:not([data-icon=loading])]:invisible'),
        // Gated+disabled: apply disabled styling manually (no native `disabled`
        // attribute, so `disabled:` pseudo-class never fires). Mirrors the
        // overrides previously applied to the TooltipTrigger branch.
        isGated &&
          disabled &&
          'cursor-not-allowed bg-disabled shadow-xs ring-disabled_subtle',
        isGated && disabled && 'text-fg-disabled hover:text-fg-disabled',
        isGated &&
          disabled &&
          'hover:bg-disabled *:data-icon:text-fg-disabled_subtle hover:*:data-icon:text-fg-disabled_subtle',
        isGated &&
          disabled &&
          'before:border-transparent hover:before:border-transparent',
        className
      )}
    >
      {/* Leading icon */}
      {isValidElement(IconLeading) && IconLeading}
      {isReactComponent(IconLeading) && (
        <IconLeading
          data-icon='leading'
          className={cx(styles.common.icon, iconLeadingClassName)}
        />
      )}

      {loading && (
        <svg
          fill='none'
          data-icon='loading'
          viewBox='0 0 20 20'
          className={cx(
            styles.common.icon,
            !showTextWhileLoading &&
              'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'
          )}
        >
          {/* Background circle */}
          <circle
            className='stroke-current opacity-30 dark:opacity-50'
            cx='10'
            cy='10'
            r='8'
            fill='none'
            strokeWidth='2'
          />
          {/* Spinning circle */}
          <circle
            className='origin-center animate-spin stroke-current'
            cx='10'
            cy='10'
            r='8'
            fill='none'
            strokeWidth='2'
            strokeDasharray='12.5 50'
            strokeLinecap='round'
          />
        </svg>
      )}

      {children !== null && children !== undefined && (
        <span
          data-text
          className={cx('transition-inherit-all', !noTextPadding && 'px-0.5')}
        >
          {children}
        </span>
      )}

      {/* Trailing icon */}
      {isValidElement(IconTrailing) && IconTrailing}
      {isReactComponent(IconTrailing) && (
        <IconTrailing
          data-icon='trailing'
          className={cx(styles.common.icon, iconTrailingClassName)}
        />
      )}
    </Component>
  );

  // Pass `title` as a normal DOM attribute so it appears in initial markup.
  // React Aria forwards unknown props to the DOM element, so this works at runtime
  // even though `title` isn't in AriaButton/AriaLink's type definitions.
  const finalButton = React.cloneElement(buttonElement, {
    ref: forwardedRef,
    ...(title ? { title } : {}),
  });

  // RETAINED old branch — links (hasHref) ONLY, byte-for-byte unchanged.
  // No consumer combines `href` with `disabledTooltip` (dormant path); the
  // guard ensures zero behavior change for the link path.
  if (hasHref && disabled && disabledTooltip) {
    // Render button content directly inside TooltipTrigger (AriaButton) to
    // avoid nesting interactive <button> elements. The TooltipTrigger itself
    // acts as the button — styled to look disabled but still focusable so
    // the tooltip can trigger on hover/focus.
    let ariaLabel = otherProps['aria-label'];
    if (!ariaLabel && typeof children === 'string') {
      ariaLabel = children;
    }
    if (!ariaLabel && typeof disabledTooltip === 'string') {
      ariaLabel = disabledTooltip;
    }

    return (
      <Tooltip
        title={disabledTooltip}
        placement='top'
        interactive={typeof disabledTooltip !== 'string'}
      >
        <TooltipTrigger
          ref={forwardedRef as React.Ref<HTMLButtonElement>}
          id={otherProps.id}
          style={otherProps.style}
          className={cx(
            styles.common.root,
            styles.sizes[size].root,
            styles.colors[color].root,
            isLinkType && styles.sizes[size].linkRoot,
            px && styles.paddingX[px],
            py && styles.paddingY[py],
            // Override color-variant backgrounds with disabled styles.
            // The `disabled:` pseudo-class won't fire on TooltipTrigger (it's
            // not a real <button disabled>), so we apply them directly —
            // including hover overrides to prevent interactive-looking states.
            'cursor-not-allowed bg-disabled shadow-xs ring-disabled_subtle',
            'text-fg-disabled hover:text-fg-disabled',
            'hover:bg-disabled *:data-icon:text-fg-disabled_subtle hover:*:data-icon:text-fg-disabled_subtle',
            // Suppress the inner border gradient from color variants
            'before:border-transparent hover:before:border-transparent',
            className
          )}
          aria-label={isIcon ? ariaLabel : undefined}
          aria-disabled='true'
          data-icon-only={isIcon ? true : undefined}
          data-testid={
            (otherProps as Record<string, unknown>)['data-testid'] as
              | string
              | undefined
          }
          {...(title ? { title } : {})}
        >
          {isValidElement(IconLeading) && IconLeading}
          {isReactComponent(IconLeading) && (
            <IconLeading
              data-icon='leading'
              className={cx(styles.common.icon, iconLeadingClassName)}
            />
          )}
          {children !== null && children !== undefined && (
            <span
              data-text
              className={cx(
                'transition-inherit-all',
                !noTextPadding && 'px-0.5'
              )}
            >
              {children}
            </span>
          )}
          {isValidElement(IconTrailing) && IconTrailing}
          {isReactComponent(IconTrailing) && (
            <IconTrailing
              data-icon='trailing'
              className={cx(styles.common.icon, iconTrailingClassName)}
            />
          )}
        </TooltipTrigger>
      </Tooltip>
    );
  }

  // Unified gated path for non-href buttons with a disabledTooltip.
  // The single AriaButton (finalButton) stays mounted at a stable tree position
  // whether enabled or disabled — the AriaButton key is constant so react-aria
  // usePress reconciles in place and an in-flight press survives a disable flip.
  //
  // isDisabled={!disabled}:
  //   disabled=true  → isDisabled=false → tooltip shows on hover/focus
  //   disabled=false → isDisabled=true  → tooltip suppressed (button is active)
  //
  // wrapperClassName (interactive path only):
  //   disabled=true  → undefined → default 'relative inline-flex' (needed for positioning)
  //   disabled=false → 'contents' → wrapper generates no layout box (no visual impact)
  //
  // Suppress the tooltip when there's nothing to show (`disabledTooltip` is
  // undefined this render) in addition to the enabled case — `wrapInTooltip`
  // can be true here with no current content (see its definition above).
  if (wrapInTooltip) {
    return (
      <Tooltip
        title={disabledTooltip}
        placement='top'
        interactive={typeof disabledTooltip !== 'string'}
        isDisabled={!disabled || disabledTooltip === undefined}
        wrapperClassName={disabled ? undefined : 'contents'}
      >
        {finalButton}
      </Tooltip>
    );
  }

  return finalButton;
});
